import { Alert, Box, Stack, styled } from '@mui/material';

import { RouterIconButton, SelectableProps, TableRow, TableRowProps } from '@components/base';
import useLayoutEntityFilterContext from '@hooks/useLayoutEntityFilter';
import LayoutEntity from '@models/LayoutEntity';
import LayoutEntityTable, {
  LayoutEntityTableProps,
} from '@modules/layouts/components/LayoutEntityTable';

type LayoutEntityListPageProps<T extends LayoutEntity> = SelectableProps &
  Pick<LayoutEntityTableProps<T>, 'TableRowComponent'> &
  Required<Pick<LayoutEntityTableProps<T>, 'columns' | 'contentType' | 'entityRoute'>> & {
    hiddenItemIds?: string[];
    typeField?: string;
  };

const StyledEntityStack = styled(Stack)`
  bottom: 0;
  height: 100%;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

type MinifiedLayoutEntityTableRowProps<T extends LayoutEntity> = TableRowProps<T> &
  Pick<LayoutEntityListPageProps<T>, 'entityRoute'>;

function MinifiedEntityTableRow<T extends LayoutEntity>({
  entityRoute,
  ...rest
}: MinifiedLayoutEntityTableRowProps<T>) {
  const { item } = rest;

  return (
    <TableRow
      {...rest}
      getActions={() => (
        <RouterIconButton icon='openInNew' target='_blank' to={entityRoute(item)} />
      )}
    />
  );
}

function MinifiedEntityList<T extends LayoutEntity>({
  columns,
  contentType,
  entityRoute,
  hiddenItemIds = [],
  selectable,
}: LayoutEntityListPageProps<T>) {
  const { filteredEntities, loading, error, hasFilters, tagValues, queryText } =
    useLayoutEntityFilterContext<T>();

  return error ? (
    <Alert severity='error'>{error.message}</Alert>
  ) : (
    <Box height={1} position='relative'>
      <StyledEntityStack width={1}>
        <LayoutEntityTable
          columns={columns}
          contentType={contentType}
          emptyStateContext={hasFilters || queryText ? 'search' : undefined}
          hasActions
          hasTags={Boolean(tagValues)}
          idKey='id'
          items={filteredEntities.filter(({ id }) => !hiddenItemIds.includes(id))}
          loading={loading}
          openEntityInNewTab
          selectable={selectable}
          TableRowComponent={props => (
            <MinifiedEntityTableRow {...props} entityRoute={entityRoute} />
          )}
        />
      </StyledEntityStack>
    </Box>
  );
}

export default MinifiedEntityList;
